import {
  Component,
  OnInit,
  OnDestroy,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '../../../services/logger.service';

import { DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CompanyService } from '../../../services/company.service';
import type { Company } from '../../../services/company.service';
import { StorefrontService } from '../../../services/storefront.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideCircleCheck,
  lucideCopy,
  lucideExternalLink,
  lucideInfo,
  lucideRadar,
  lucideTriangleAlert,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';

@Component({
  selector: 'app-integration-settings',
  imports: [
    DatePipe,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmItemImports,
  ],
  providers: [
    provideIcons({
      lucideCheck,
      lucideCircleCheck,
      lucideCopy,
      lucideExternalLink,
      lucideInfo,
      lucideRadar,
      lucideTriangleAlert,
      lucideUsers,
    }),
  ],
  templateUrl: './integration-settings.component.html',
  styleUrl: './integration-settings.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class IntegrationSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  company: Company | null = null;
  copiedKey: string | null = null;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  private markCopied(key: string) {
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    this.copiedKey = key;
    this.copiedTimer = setTimeout(() => {
      this.copiedKey = null;
    }, 2000);
  }

  constructor(
    private companyService: CompanyService,
    private logger: LoggerService,
    private storefrontService: StorefrontService,
  ) {}

  get effectiveStoreId(): string {
    if (this.company?.is_member && this.company.group_company_id) {
      return this.company.group_company_id;
    }
    return this.company?.public_id || 'DEINE_STORE_ID';
  }

  get effectiveSuburl(): string | null {
    if (this.company?.is_member && this.company.group_company_suburl) {
      return this.company.group_company_suburl;
    }
    return this.company?.suburl || null;
  }

  get storefrontUrl(): string {
    const suburl = this.effectiveSuburl;
    if (!suburl) return '';
    const protocol = this.storefrontService.getProtocol();
    const domain = this.storefrontService.getStorefrontBaseDomain();
    return `${protocol}${suburl}.${domain}`;
  }

  /** How the backend first saw the shop on the website. */
  get detectedVia(): string {
    return this.company?.integration_method === 'plugin' ? 'Plugin' : 'Link';
  }

  /** Platform guides for embedding the plugin. */
  readonly tutorials = [
    {
      name: 'Wix',
      hint: 'Custom Code einbinden',
      url: 'https://support.wix.com/de/article/wix-editor-hinzufuegen-von-benutzerdefiniertem-code-zu-deiner-website',
    },
    {
      name: 'WordPress',
      hint: 'Code in Header einbinden',
      url: 'https://wordpress.com/support/adding-code-to-headers/',
    },
    {
      name: 'Squarespace',
      hint: 'Code-Injection nutzen',
      url: 'https://support.squarespace.com/hc/de/articles/205815908',
    },
    {
      name: 'Webflow',
      hint: 'Custom Code im Projekt',
      url: 'https://university.webflow.com/lesson/custom-code-in-the-head-and-body-tags',
    },
  ];

  copyUrl() {
    navigator.clipboard
      .writeText(this.storefrontUrl)
      .then(() => this.markCopied('url'))
      .catch(() => this.logger.error('Failed to copy URL to clipboard'));
  }

  ngOnInit() {
    this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company: Company) => {
      this.company = company;
    });
  }

  copyToClipboard(type: 'popup' | 'standalone') {
    const storeId = this.effectiveStoreId;
    const shopName = this.company?.is_member
      ? this.company.member_of_company_name || 'Netzwerk'
      : this.company?.name || 'Mein Shop';

    const popupCode = `
      <!-- Füge diesen Code in den Head-Bereich deiner Seite ein -->
      <script type="module" src="https://cdn.schenkwerk.com/schenkwerk-voucher.js"></script>

      <!-- Füge diesen Code an der gewünschten Stelle in deiner Seite ein -->
      <schenkwerk-voucher
        storeId="${storeId}"
        asWidget>
      </schenkwerk-voucher>
    `;

    const standaloneCode = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Gutscheine - ${shopName}</title>
        
        <!-- Schenkwerk Voucher Plugin -->
        <script type="module" src="https://cdn.schenkwerk.com/schenkwerk-voucher.js"></script>
      </head>
      <body>
        <!-- Gutschein-Widget -->
        <schenkwerk-voucher
          storeId="${storeId}">
        </schenkwerk-voucher>
      </body>
      </html>
    `;

    const code = type === 'popup' ? popupCode : standaloneCode;

    navigator.clipboard
      .writeText(code)
      .then(() => this.markCopied(type))
      .catch(() => this.logger.error('Failed to copy to clipboard'));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  }
}
